import { type ReactElement, type ReactNode } from "react";
import { Popover, type PopoverProps } from "antd";

import { cn } from "@fitness-app/utils/src/styles/cn";

export type PopoverTone = "indigo" | "amber" | "emerald" | "sky" | "rose" | "blue" | "gray";

const ICON_TONE: Record<PopoverTone, string> = {
  indigo: "bg-indigo-50 text-indigo-600",
  amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600",
  sky: "bg-sky-50 text-sky-600",
  rose: "bg-rose-50 text-rose-500",
  blue: "bg-blue-50 text-blue-600",
  gray: "bg-gray-100 text-gray-500",
};

export type PillTone = "green" | "amber" | "red" | "blue" | "gray";

const PILL_TONE: Record<PillTone, string> = {
  green: "bg-green-50 text-green-700 ring-green-600/20 before:bg-green-500",
  amber: "bg-amber-50 text-amber-800 ring-amber-600/20 before:bg-amber-500",
  red: "bg-red-50 text-red-700 ring-red-600/20 before:bg-red-500",
  blue: "bg-blue-50 text-blue-700 ring-blue-600/20 before:bg-blue-500",
  gray: "bg-gray-50 text-gray-600 ring-gray-500/20 before:bg-gray-400",
};

export const StatusPill = ({ tone, children }: { tone: PillTone; children: ReactNode }) => (
  <span
    className={cn(
      "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
      "before:size-1.5 before:rounded-full before:content-['']",
      PILL_TONE[tone],
    )}
  >
    {children}
  </span>
);

export const PopoverSection = ({ label, children }: { label?: ReactNode; children: ReactNode }) => (
  <section className="flex flex-col gap-2 [&+&]:mt-4">
    {label && <h4 className="m-0 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</h4>}
    {children}
  </section>
);

export const DetailRow = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <div className="grid grid-cols-[112px_minmax(0,1fr)] items-center gap-3 text-[13px]">
    <span className="text-gray-500">{label}</span>
    <div className="min-w-0 font-medium text-gray-900">{children}</div>
  </div>
);

interface EventPopoverCardProps {
  icon: ReactNode;
  tone: PopoverTone;
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export const EventPopoverCard = ({
  icon,
  tone,
  title,
  subtitle,
  badge,
  footer,
  children,
  className,
}: EventPopoverCardProps) => (
  <div className={cn("flex w-[320px] max-w-[calc(100vw-32px)] flex-col", className)}>
    <header className="flex items-start gap-2.5 px-3.5 py-3">
      <div
        className={cn("flex size-8 shrink-0 items-center justify-center rounded-lg [&_svg]:size-4", ICON_TONE[tone])}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold leading-5 text-gray-900">{title}</div>
        {subtitle && <div className="mt-0.5 text-xs text-gray-500 first-letter:uppercase">{subtitle}</div>}
        {/* Pod datą, nie obok tytułu: „Trening wykonany częściowo" ścisnąłby datę do trzech linii. */}
        {badge && <div className="mt-2 flex">{badge}</div>}
      </div>
    </header>
    {children && (
      <div className="max-h-[min(460px,65vh)] overflow-y-auto border-0 border-t border-solid border-gray-100 px-3.5 py-3">
        {children}
      </div>
    )}
    {footer && (
      <footer className="flex items-center justify-end gap-2 border-0 border-t border-solid border-gray-100 bg-gray-50/80 px-3.5 py-2">
        {footer}
      </footer>
    )}
  </div>
);

type EventPopoverProps = Omit<PopoverProps, "title" | "children" | "styles"> & { children: ReactElement };

// Wspólna oprawa popoverów kalendarza: strzałka wskazuje chip, padding rysuje karta.
export const EventPopover = ({ children, ...props }: EventPopoverProps) => (
  <Popover
    placement="right"
    arrow={{ pointAtCenter: true }}
    mouseEnterDelay={0.1}
    styles={{ body: { padding: 0, borderRadius: 10, overflow: "hidden" } }}
    {...props}
  >
    {children}
  </Popover>
);
